import { useState, useMemo, useRef, useEffect, lazy, Suspense } from 'react'
import { RefreshCw, Calendar, CheckCircle, Clock, Flame, Zap, Moon, Target, UtensilsCrossed, ChevronLeft, ChevronRight, FolderOpen, ListTodo, Repeat, Edit2, Inbox, LayoutGrid, List, CalendarClock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { TaskFinder } from '@/components/TaskFinder'
import { QuickCapture } from '@/components/QuickCapture'
import { HabitInstanceEditor, HabitInstanceBadge } from '@/components/HabitInstanceEditor'
import { TaskDetailDialog } from '@/components/TaskDetailDialog'
import { EmptyState, emptyStateConfigs } from '@/components/EmptyState'
import { TodaySidebar } from '@/components/sidebar'
import { TodayByProject } from '@/components/TodayByProject'
import { CalendarEventGroup } from '@/components/CalendarEventGroup'
import { useApp } from '@/context/AppContext'
import { useDeadlines } from '@/hooks/useDeadlines'
import { useToday } from '@/hooks/useToday'
import { useCalendar } from '@/context/CalendarContext'
import type { Schedule, Task, ScheduledTask, Project } from '@/types'
import { groupHasPreference, groupOverlappingEvents, isWorkoutHabit, type CalendarEvent, formatClock12, formatDuration } from '@trackmind/core'

// The gym card carries the whole program (next weights, catch-up rules), so
// it loads with the Fitness chunk rather than with Today.
const WorkoutTodayCard = lazy(() => import('@/components/fitness/WorkoutTodayCard'))
import { cn } from '@/lib/utils'
import {
  computeScheduleStats, busyConflicts, emptyPlanReason,
} from '@trackmind/core'
import { InfoTip } from '@/components/ui/tooltip'
import { TaskInfoTip } from '@/components/TaskInfoTip'

// Inline editable actual time component for completed tasks
function ActualTimeEditor({ task, estimatedMinutes }: { task: Task; estimatedMinutes: number }) {
  const { saveTask } = useApp()
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(task.actual_minutes?.toString() || '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    const minutes = parseInt(value, 10)
    if (!isNaN(minutes) && minutes >= 0) {
      await saveTask({
        ...task,
        actual_minutes: minutes,
        updated_at: new Date().toISOString(),
      })
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setValue(task.actual_minutes?.toString() || '')
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input
          ref={inputRef}
          type="number"
          min="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="h-6 w-16 text-xs px-1"
          placeholder={estimatedMinutes.toString()}
        />
        <span className="text-xs text-muted-foreground">min</span>
      </div>
    )
  }

  const hasActual = task.actual_minutes !== null && task.actual_minutes !== undefined
  const actualDisplay = hasActual ? formatDuration(task.actual_minutes!) : null
  const estimatedDisplay = formatDuration(estimatedMinutes)

  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        setValue(task.actual_minutes?.toString() || estimatedMinutes.toString())
        setIsEditing(true)
      }}
      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground group"
      title="Click to log actual time"
    >
      {hasActual ? (
        <>
          <span className="line-through text-xs">{estimatedDisplay}</span>
          <span className="text-green-600 font-medium">{actualDisplay}</span>
        </>
      ) : (
        <>
          <span>{estimatedDisplay}</span>
          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50" />
        </>
      )}
    </button>
  )
}

interface TodayViewProps {
  schedule: Schedule | null
  tasks: Task[]
  projects: Project[]
  loading: boolean
  onReschedule: () => Promise<void>
  onCompleteTask: (id: string, forDate?: string) => void
  /** Untick a completed row to take the completion back. */
  onUncompleteTask?: (id: string, forDate?: string) => void
  onEditTask: (task: Task) => void
  selectedDate?: string // YYYY-MM-DD format
  onDateChange?: (date: string) => void
  // Inbox integration
  inboxTasks?: Task[]
  onCaptureTask?: (data: import('@/types').CreateTaskInput) => Promise<import('@/types').Task | void>
  onCreateProject?: (name: string) => Promise<Project>
  onOpenInbox?: () => void
  /**
   * Open Fitness. `start` = begin (or resume) the session straight away —
   * what tapping the Workout habit means, since it is ticked by finishing a
   * workout rather than by its checkbox.
   */
  onOpenWorkout?: (start: boolean) => void
}

/**
 * One entry in the day timeline: either something we scheduled, or a meeting
 * from a connected calendar. Meetings are woven in by start time so the day
 * reads as one list — the scheduler has already left their time free, and
 * showing them here is what makes those gaps make sense.
 */
type TimelineRow =
  | { kind: 'task'; scheduled: ScheduledTask; index: number }
  // A group, not a single event: two calendars booked over each other are one
  // thing to reckon with, not two unrelated rows to compare by eye.
  | { kind: 'events'; events: CalendarEvent[] }

const energyIcons = {
  high: <Flame className="h-4 w-4 text-orange-500" />,
  medium: <Zap className="h-4 w-4 text-yellow-500" />,
  low: <Moon className="h-4 w-4 text-blue-400" />,
}

export function TodayView({
  schedule,
  tasks,
  projects,
  loading,
  onReschedule,
  onCompleteTask,
  onUncompleteTask,
  onEditTask,
  selectedDate,
  onDateChange,
  inboxTasks = [],
  onCaptureTask,
  onCreateProject,
  onOpenInbox,
  onOpenWorkout,
}: TodayViewProps) {
  const deadlines = useDeadlines()
  const todayKey = useToday()
  const { eventsForDate, busySlotsForDate, sources: calendarSources } = useCalendar()
  const [editingHabit, setEditingHabit] = useState<Task | null>(null)
  const [instanceRefreshKey, setInstanceRefreshKey] = useState(0)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [selectedScheduledTask, setSelectedScheduledTask] = useState<ScheduledTask | null>(null)
  const [scheduleFilter, setScheduleFilter] = useState<'all' | 'tasks' | 'habits'>('all')
  const [showDoneAhead, setShowDoneAhead] = useState(false)
  const [layoutMode, setLayoutMode] = useState<'schedule' | 'projects'>(() => {
    try {
      const saved = localStorage.getItem('today.layoutMode')
      return saved === 'projects' ? 'projects' : 'schedule'
    } catch {
      return 'schedule'
    }
  })
  useEffect(() => {
    try { localStorage.setItem('today.layoutMode', layoutMode) } catch { /* ignore */ }
  }, [layoutMode])

  const getTaskById = (id: string) => tasks.find((t) => t.id === id)
  const getProjectById = (id: string) => projects.find((p) => p.id === id)

  // The user's today (their zone setting) — the day useSchedule loads.
  const todayDateStr = todayKey()
  const currentDate = selectedDate || todayDateStr

  const handleTaskClick = (scheduled: ScheduledTask) => {
    setSelectedTaskId(scheduled.task_id)
    setSelectedScheduledTask(scheduled)
  }

  const handleCloseDetail = () => {
    setSelectedTaskId(null)
    setSelectedScheduledTask(null)
  }

  const selectedTask = selectedTaskId ? getTaskById(selectedTaskId) : null
  const selectedProject = selectedTask ? getProjectById(selectedTask.project_id) : null
  const isToday = currentDate === todayDateStr

  /**
   * Every "mark done" on Today goes through here. The Workout habit is the
   * exception: on today, its tick is FINISHING A WORKOUT, so the control
   * opens the session instead. On other days it ticks like any habit, which
   * is how a missed day gets filled in by hand.
   */
  const completeFromToday = (taskId: string, forDate?: string) => {
    if (isToday && onOpenWorkout && isWorkoutHabit(tasks.find((t) => t.id === taskId))) {
      onOpenWorkout(true)
      return
    }
    onCompleteTask(taskId, forDate)
  }

  // Navigate to previous/next day
  const navigateDay = (direction: 'prev' | 'next') => {
    if (!onDateChange) return
    // Parse currentDate as local date (not UTC)
    const [year, month, day] = currentDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    if (direction === 'prev') {
      date.setDate(date.getDate() - 1)
    } else {
      date.setDate(date.getDate() + 1)
    }
    // Format as local date string
    const newDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    onDateChange(newDateStr)
  }

  const goToToday = () => {
    if (onDateChange) {
      onDateChange(todayDateStr)
    }
  }

  const isTaskCompleted = (taskId: string) => {
    const task = getTaskById(taskId)

    // For habits: ONLY check the day-specific completed_tasks list
    // Habits are recurring, so completion is per-day, not global
    if (task?.is_habit) {
      return (schedule?.completed_tasks || []).includes(taskId)
    }

    // For regular tasks: check both day-specific completion AND global status
    // Once a regular task is completed, it stays completed
    return (schedule?.completed_tasks || []).includes(taskId) ||
           task?.status === 'completed'
  }

  // Helper to convert time string to minutes
  const timeToMinutes = (timeStr: string): number => {
    const [h, m] = timeStr.split(':').map(Number)
    return h * 60 + m
  }

  // Check if a scheduled task's time slot is in the past (only for today)
  const isTaskInPast = (scheduled: ScheduledTask): boolean => {
    if (!isToday) return false
    const now = new Date()
    const currentMinutes = now.getHours() * 60 + now.getMinutes()
    const taskStartMinutes = timeToMinutes(scheduled.start_time)
    let taskEndMinutes = timeToMinutes(scheduled.end_time)

    // Handle midnight end time: "00:00" at end of day should be 1440, not 0
    // A task ending at "00:00" that starts in the evening (like 23:45-00:00) ends at midnight
    if (taskEndMinutes === 0 && taskStartMinutes > 720) {
      taskEndMinutes = 1440  // Treat as end of day
    }

    // For overnight schedules, we need to handle wrap-around
    const tasks = schedule?.scheduled_tasks || []
    const { isOvernight } = detectOvernightSchedule(tasks)

    if (isOvernight && tasks.length > 0) {
      const scheduleStartMin = timeToMinutes(tasks[0].start_time)
      const normCurrent = normalizeTime(currentMinutes, isOvernight, scheduleStartMin)
      const normTaskEnd = normalizeTime(taskEndMinutes, isOvernight, scheduleStartMin)
      return normTaskEnd < normCurrent
    }

    return taskEndMinutes < currentMinutes
  }

  // Detect if the schedule spans overnight (has tasks both before and after midnight)
  const detectOvernightSchedule = (tasks: ScheduledTask[]): { isOvernight: boolean; pivotIndex: number } => {
    if (tasks.length < 2) return { isOvernight: false, pivotIndex: -1 }

    // Find where times "wrap around" - where a task starts at a much smaller time than previous ended
    for (let i = 1; i < tasks.length; i++) {
      const prevEnd = timeToMinutes(tasks[i - 1].end_time)
      const currStart = timeToMinutes(tasks[i].start_time)
      // If current start is much smaller than previous end (e.g., 00:00 after 23:00), it's the pivot
      if (prevEnd > 720 && currStart < 720 && prevEnd - currStart > 720) {
        return { isOvernight: true, pivotIndex: i }
      }
    }
    return { isOvernight: false, pivotIndex: -1 }
  }

  // Normalize time for overnight schedule comparisons
  // Times after midnight in an overnight schedule are treated as 1440+ for ordering
  const normalizeTime = (minutes: number, isOvernight: boolean, scheduleStartMin: number): number => {
    if (!isOvernight) return minutes
    // If the schedule starts in PM (after noon) and this time is in AM (before noon),
    // add 1440 to treat it as "next day"
    if (scheduleStartMin >= 720 && minutes < 720) {
      return minutes + 1440
    }
    return minutes
  }

  // Calculate current time indicator position
  // Returns { taskIndex, progressPercent, remainingMinutes, elapsedMinutes, totalMinutes }
  // where taskIndex is which row and progressPercent is how far through that task
  const getCurrentTimeIndicator = () => {
    const tasks = schedule?.scheduled_tasks || []
    if (!schedule || tasks.length === 0 || !isToday) return null

    const now = new Date()
    const currentMinutes = now.getHours() * 60 + now.getMinutes()

    const { isOvernight } = detectOvernightSchedule(tasks)
    const scheduleStartMin = timeToMinutes(tasks[0].start_time)
    const normalizedCurrent = normalizeTime(currentMinutes, isOvernight, scheduleStartMin)

    // Find which task the current time falls within
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i]
      const taskStart = timeToMinutes(task.start_time)
      const taskEnd = timeToMinutes(task.end_time)

      // Normalize for overnight comparison
      const normStart = normalizeTime(taskStart, isOvernight, scheduleStartMin)
      const normEnd = normalizeTime(taskEnd, isOvernight, scheduleStartMin)
      const totalMinutes = normEnd - normStart

      if (normalizedCurrent >= normStart && normalizedCurrent <= normEnd) {
        // Current time is within this task
        const elapsedMinutes = normalizedCurrent - normStart
        const remainingMinutes = normEnd - normalizedCurrent
        const progressPercent = totalMinutes > 0 ? (elapsedMinutes / totalMinutes) * 100 : 0
        return { taskIndex: i, progressPercent, remainingMinutes, elapsedMinutes, totalMinutes }
      }
    }

    // Check if current time is before first task or after last task
    const firstTask = tasks[0]
    const lastTask = tasks[tasks.length - 1]
    const scheduleStart = normalizeTime(timeToMinutes(firstTask.start_time), isOvernight, scheduleStartMin)
    const scheduleEnd = normalizeTime(timeToMinutes(lastTask.end_time), isOvernight, scheduleStartMin)

    if (normalizedCurrent < scheduleStart || normalizedCurrent > scheduleEnd) return null

    // Current time is between tasks - find the gap
    for (let i = 0; i < tasks.length - 1; i++) {
      const currentTask = tasks[i]
      const nextTask = tasks[i + 1]
      const currEnd = normalizeTime(timeToMinutes(currentTask.end_time), isOvernight, scheduleStartMin)
      const nextStart = normalizeTime(timeToMinutes(nextTask.start_time), isOvernight, scheduleStartMin)

      if (normalizedCurrent > currEnd && normalizedCurrent < nextStart) {
        // We're in a gap between tasks, show at end of current task
        return { taskIndex: i, progressPercent: 100, remainingMinutes: 0, elapsedMinutes: 0, totalMinutes: 0 }
      }
    }

    return null
  }

  const currentTimeIndicator = getCurrentTimeIndicator()

  // Calculate overall day progress (across the entire schedule)
  // Handles overnight schedules where end time < start time
  const getDayProgress = () => {
    const tasks = schedule?.scheduled_tasks || []
    if (!schedule || tasks.length === 0 || !isToday) return null

    const now = new Date()
    const currentMinutes = now.getHours() * 60 + now.getMinutes()

    const firstTask = tasks[0]
    const lastTask = tasks[tasks.length - 1]

    const scheduleStartMin = timeToMinutes(firstTask.start_time)
    let scheduleEndMin = timeToMinutes(lastTask.end_time)

    // Handle midnight end time: "00:00" should be 1440 when schedule starts earlier in the day
    if (scheduleEndMin === 0 && scheduleStartMin > 0) {
      scheduleEndMin = 1440  // Treat as end of day (midnight)
    }

    const { isOvernight } = detectOvernightSchedule(tasks)

    // Normalize times for overnight schedules
    const normStart = scheduleStartMin
    const normEnd = normalizeTime(scheduleEndMin, isOvernight, scheduleStartMin)
    const normCurrent = normalizeTime(currentMinutes, isOvernight, scheduleStartMin)

    const totalScheduleMinutes = normEnd - normStart

    if (normCurrent < normStart) {
      return { percent: 0, elapsed: 0, remaining: totalScheduleMinutes, total: totalScheduleMinutes, startTime: firstTask.start_time, endTime: lastTask.end_time }
    }
    if (normCurrent > normEnd) {
      return { percent: 100, elapsed: totalScheduleMinutes, remaining: 0, total: totalScheduleMinutes, startTime: firstTask.start_time, endTime: lastTask.end_time }
    }

    const elapsed = normCurrent - normStart
    const remaining = normEnd - normCurrent
    const percent = totalScheduleMinutes > 0 ? (elapsed / totalScheduleMinutes) * 100 : 0

    return { percent, elapsed, remaining, total: totalScheduleMinutes, startTime: firstTask.start_time, endTime: lastTask.end_time }
  }

  const dayProgress = getDayProgress()



  // Day-plan stats derived from the schedule itself (see computeScheduleStats
  // in core for why the stored summary counters can't be trusted: off-plan
  // completions used to push the rate above 100% and then DOWN as more
  // completed). Deriving also self-heals summaries the old code corrupted.

  // Calculate vertical progress position based on task rows (not time)
  // Accounts for varying row heights (task rows are taller than break rows)
  const getVerticalProgressPositions = () => {
    const tasks = schedule?.scheduled_tasks || []
    if (!currentTimeIndicator || !schedule || tasks.length === 0) return null

    const currentRowIndex = currentTimeIndicator.taskIndex
    const progressWithinRow = currentTimeIndicator.progressPercent / 100

    // Calculate row weights based on type (tasks are taller than breaks)
    // Task rows: ~65px (py-3 + content + project info)
    // Break rows: ~40px (py-2 + simple content)
    const TASK_WEIGHT = 1.0
    const BREAK_WEIGHT = 0.6

    // Calculate the weight for each row
    const rowWeights = tasks.map(st => st.is_buffer ? BREAK_WEIGHT : TASK_WEIGHT)
    const totalWeight = rowWeights.reduce((sum, w) => sum + w, 0)

    // Calculate cumulative position up to current row
    let cumulativeWeight = 0
    for (let i = 0; i < currentRowIndex; i++) {
      cumulativeWeight += rowWeights[i]
    }

    const rowStartPercent = (cumulativeWeight / totalWeight) * 100
    const currentRowWeight = rowWeights[currentRowIndex]
    const rowHeightPercent = (currentRowWeight / totalWeight) * 100

    // Dot position is within the current row based on progress
    const dotPosition = rowStartPercent + (progressWithinRow * rowHeightPercent)

    return {
      fillPosition: Math.min(100, Math.max(0, rowStartPercent)),
      dotPosition: Math.min(100, Math.max(0, dotPosition))
    }
  }

  const verticalProgress = getVerticalProgressPositions()

  // Count habits for display
  const habitTasks = (schedule?.scheduled_tasks || []).filter(st => {
    const task = getTaskById(st.task_id)
    return task?.is_habit && !st.is_buffer
  })

  // Filter scheduled tasks, and lift out rows a RESCHEDULE has retired.
  //
  // The trigger is `done_ahead`, which only the scheduler sets, and only when
  // it re-anchors a completed row because its planned slot was still ahead —
  // i.e. the moment that slot was handed to other work. Until you press
  // Reschedule nothing moves: ticking a task in the future leaves it exactly
  // where it was, struck through, in place. That was the behaviour before any
  // of this and it stays the behaviour; reordering or ticking things must
  // never rearrange the day under you.
  const { filteredScheduledTasks, doneAheadTasks } = useMemo(() => {
    const allTasks = schedule?.scheduled_tasks || []
    const matchesFilter = (st: ScheduledTask) => {
      if (scheduleFilter === 'all') return true
      if (st.is_buffer) return true
      const task = getTaskById(st.task_id)
      if (!task) return false
      return scheduleFilter === 'habits' ? task.is_habit : !task.is_habit
    }

    const timeline: ScheduledTask[] = []
    const doneAhead: ScheduledTask[] = []
    for (const st of allTasks) {
      if (!matchesFilter(st)) continue
      if (st.done_ahead && isTaskCompleted(st.task_id)) doneAhead.push(st)
      else timeline.push(st)
    }
    return { filteredScheduledTasks: timeline, doneAheadTasks: doneAhead }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule?.scheduled_tasks, schedule?.completed_tasks, scheduleFilter, tasks])

  // Meetings from connected calendars, woven into the day by start time.
  const dayEvents = eventsForDate(currentDate)
  const sourcesById = useMemo(
    () => new Map(calendarSources.map((s) => [s.id, s])),
    [calendarSources],
  )
  const sourceColor = useMemo(() => {
    const map = new Map(calendarSources.map((s) => [s.id, s.color]))
    return (sourceId: string) => map.get(sourceId) ?? null
  }, [calendarSources])
  const sourceName = useMemo(() => {
    const map = new Map(calendarSources.map((s) => [s.id, s.name]))
    return (sourceId: string) => map.get(sourceId) ?? null
  }, [calendarSources])

  // Overlapping meetings are grouped rather than resolved — see
  // CalendarEventGroup for why nothing is ever hidden.
  const dayEventGroups = useMemo(
    () => groupOverlappingEvents(dayEvents, sourcesById),
    [dayEvents, sourcesById],
  )

  // Meetings join the plan: a day with five of them and no tasks read 0%,
  // which is false. Only calendars the user lets occupy time count — one
  // excluded from scheduling isn't part of the day's plan by their own
  // instruction. See computeScheduleStats for the semantics.
  const planMeetings = useMemo(
    () =>
      dayEvents
        .filter((e) => !e.all_day && (sourcesById.get(e.source_id)?.blocks_time ?? false))
        .map((e) => ({ start: e.start, end: e.end, all_day: e.all_day })),
    [dayEvents, sourcesById],
  )
  const dayStats = computeScheduleStats(schedule ?? {}, { meetings: planMeetings })

  // Why the plan is empty, when there's a crisp answer. Reads the hours off
  // the schedule the scheduler actually built, so an overnight shift
  // (15:00–05:00) is judged by its own window rather than a daytime one.
  // Core's emptyPlanReason — the assistant gives the same sentence.
  const planEmptyReason = useMemo(
    () => emptyPlanReason(schedule, tasks, projects, { date: currentDate, today: todayKey() }),
    [schedule, tasks, projects, currentDate, todayKey],
  )
  // Said once, shown on the phone strip and on the desktop Progress tile.
  // The denominator is the part people misread — it is the size of the plan,
  // not the number of things you could conceivably do today.
  const dayStatsExplainer = (
    <span className="block space-y-0.5">
      <span className="block font-medium">
        {dayStats.planDone} of {dayStats.planSize} planned tasks done
        {dayStats.meetingsPlanned > 0
          ? `, plus ${dayStats.meetingsDone} of ${dayStats.meetingsPlanned} meetings`
          : ''}
      </span>
      <span className="block text-muted-foreground">
        The denominator is today's plan and doesn't move, so the bar only goes up.
      </span>
      {dayStats.offPlanDone > 0 && (
        <span className="block text-muted-foreground">
          {dayStats.offPlanDone} finished off-plan — counted under Completed, not in the percentage.
        </span>
      )}
    </span>
  )


  // Meetings no longer displace habits or hard-pinned tasks, so where they
  // overlap we have to say so — an unexplained double-booking is worse than
  // either resolution.
  const dayBusySlots = useMemo(() => busySlotsForDate(currentDate), [busySlotsForDate, currentDate])

  const timelineRows = useMemo<TimelineRow[]>(() => {
    // `index` must keep counting scheduled tasks only — the current-time
    // indicator addresses rows by task position, so meetings mustn't shift it.
    const rows: TimelineRow[] = []
    const pending = [...dayEventGroups]
    let taskIndex = 0

    const startMinutes = (group: CalendarEvent[]) => {
      if (group.every((e) => e.all_day)) return -1
      return Math.min(
        ...group.filter((e) => !e.all_day).map((e) => {
          const d = new Date(e.start)
          return d.getHours() * 60 + d.getMinutes()
        }),
      )
    }
    const toMinutes = (time: string) => {
      const [h, m] = time.split(':').map(Number)
      return (h || 0) * 60 + (m || 0)
    }

    for (const scheduled of filteredScheduledTasks) {
      const at = toMinutes(scheduled.start_time)
      while (pending.length > 0 && startMinutes(pending[0]) <= at) {
        rows.push({ kind: 'events', events: pending.shift()! })
      }
      rows.push({ kind: 'task', scheduled, index: taskIndex++ })
    }
    for (const events of pending) rows.push({ kind: 'events', events })
    return rows
  }, [filteredScheduledTasks, dayEventGroups])

  // Find next upcoming task (handles overnight schedules)
  const findNextTask = (): ScheduledTask | null => {
    if (!schedule) return null

    const tasks = schedule.scheduled_tasks || []
    if (tasks.length === 0) return null

    const now = new Date()
    const currentMinutes = now.getHours() * 60 + now.getMinutes()

    const { isOvernight } = detectOvernightSchedule(tasks)
    const scheduleStartMin = timeToMinutes(tasks[0].start_time)
    const normCurrent = normalizeTime(currentMinutes, isOvernight, scheduleStartMin)

    for (const st of tasks) {
      if (st.is_buffer) continue
      const taskStartMin = timeToMinutes(st.start_time)
      const normTaskStart = normalizeTime(taskStartMin, isOvernight, scheduleStartMin)

      if (normTaskStart > normCurrent && !isTaskCompleted(st.task_id)) {
        return st
      }
    }
    return null
  }

  const nextTask = findNextTask()
  const nextTaskDetails = nextTask ? getTaskById(nextTask.task_id) : null

  // Convert date string to Date object for sidebar
  const selectedDateObj = useMemo(() => {
    const [year, month, day] = currentDate.split('-').map(Number)
    return new Date(year, month - 1, day)
  }, [currentDate])

  // Handle date selection from sidebar calendar
  const handleSidebarDateSelect = (date: Date) => {
    if (!onDateChange) return
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    onDateChange(dateStr)
  }

  return (
    <div className="flex flex-col gap-6 md:flex-row">
      {/* Main Content */}
      <div className="flex-1 space-y-3 min-w-0 md:space-y-6">
        {/* Quick Capture + Inbox strip */}
        <div className="space-y-2">
          {onCaptureTask && (
            <div data-help="quick-capture">
              <QuickCapture projects={projects} onCapture={onCaptureTask} onCreateProject={onCreateProject} />
            </div>
          )}
          {onOpenInbox && inboxTasks.length > 0 && (
            <button
              onClick={onOpenInbox}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
            >
              <Inbox className="h-4 w-4 shrink-0" />
              <span className="font-medium">{inboxTasks.length} unprocessed {inboxTasks.length === 1 ? 'item' : 'items'} in Inbox</span>
              <span className="ml-auto text-xs opacity-70">Review →</span>
            </button>
          )}
        </div>

        {/* Header with summary stats */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            {/* Page title is redundant on phones — the bottom nav already says
                "Today"; the date row below becomes the mobile heading */}
            <h2 className="hidden md:flex text-2xl font-bold items-center gap-2">
              <Target className="h-6 w-6" />
              {isToday ? "Today's Focus" : "Schedule"}
            </h2>
            <div className="flex items-center gap-2 md:mt-1">
              {onDateChange && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigateDay('prev')}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <p className="min-w-[180px] text-center font-semibold text-foreground md:font-normal md:text-muted-foreground">
                {new Date(currentDate + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              {onDateChange && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigateDay('next')}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
              {onDateChange && !isToday && (
                <Button variant="outline" size="sm" className="ml-2" onClick={goToToday}>
                  Today
                </Button>
              )}
              {/* Phone: reschedule lives beside the date so the toggles fit one row */}
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto h-8 w-8 md:hidden"
                onClick={onReschedule}
                disabled={loading}
                title="Reschedule All"
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            {/* Layout Toggle: Schedule timeline vs. grouped by project */}
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant={layoutMode === 'schedule' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setLayoutMode('schedule')}
                className="h-7 px-1.5 text-xs md:px-2 md:text-sm gap-1"
                title="Schedule timeline"
              >
                <List className="h-3 w-3" />
                Schedule
              </Button>
              <Button
                variant={layoutMode === 'projects' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setLayoutMode('projects')}
                className="h-7 px-1.5 text-xs md:px-2 md:text-sm gap-1"
                title="Grouped by project"
              >
                <LayoutGrid className="h-3 w-3" />
                By Project
              </Button>
            </div>
            {/* Schedule Filter Toggle */}
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant={scheduleFilter === 'all' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setScheduleFilter('all')}
                className="h-7 px-1.5 text-xs md:px-2 md:text-sm"
              >
                All
              </Button>
              <Button
                variant={scheduleFilter === 'tasks' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setScheduleFilter('tasks')}
                className="h-7 px-1.5 text-xs md:px-2 md:text-sm gap-1"
              >
                <ListTodo className="h-3 w-3" />
                Tasks
              </Button>
              <Button
                variant={scheduleFilter === 'habits' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setScheduleFilter('habits')}
                className="h-7 px-1.5 text-xs md:px-2 md:text-sm gap-1"
              >
                <Repeat className="h-3 w-3" />
                Habits
              </Button>
            </div>
            <Button
              variant="outline"
              className="hidden md:inline-flex"
              onClick={onReschedule}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
              Reschedule All
            </Button>
          </div>
        </div>

      {/* The gym, when it's a training day or one is owed — above the plan,
          and shown whether or not a plan has been generated yet. */}
      {isToday && onOpenWorkout && (
        <Suspense fallback={null}>
          <WorkoutTodayCard onOpenFitness={() => onOpenWorkout(false)} />
        </Suspense>
      )}

      {!schedule ? (
        <>
          {/* No schedule doesn't mean an empty day. If calendars are
              connected, the meetings are real commitments and must be shown
              — telling someone with four meetings that they have nothing on
              is worse than useless. */}
          {dayEvents.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  From your calendar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {dayEventGroups.map((group) => (
                  <CalendarEventGroup
                    key={group[0].id}
                    events={group}
                    colorFor={sourceColor}
                    nameFor={sourceName}
                    hasPreference={groupHasPreference(group, sourcesById)}
                  />
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Schedule Generated</h3>
              <p className="text-muted-foreground mb-4">
                {dayEvents.length > 0
                  ? 'Generate a schedule to plan work around these meetings.'
                  : 'Click "Reschedule All" to generate your schedule for today.'}
              </p>
              <Button onClick={onReschedule} disabled={loading}>
                <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
                Generate Schedule
              </Button>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Progress Overview — compact one-line strip on phones, cards on md+ */}
          {/* The phone strip is four numbers with no room to explain any of
              them; a tap on it says what they mean. There is no hover on a
              touchscreen, so this is the only place the explanation can go. */}
          <InfoTip side="bottom" label={dayStatsExplainer}>
          <div className="md:hidden cursor-help rounded-lg border bg-card px-3 py-2">
            <div className="flex items-center justify-between text-sm">
              <span>
                <strong>{dayStats.planDone}/{dayStats.planSize}</strong>
                <span className="text-muted-foreground"> planned</span>
                {dayStats.offPlanDone > 0 && (
                  <span className="text-muted-foreground"> +{dayStats.offPlanDone}</span>
                )}
                {dayStats.meetingsPlanned > 0 && (
                  <span className="text-muted-foreground">
                    {' '}· {dayStats.meetingsDone}/{dayStats.meetingsPlanned} met
                  </span>
                )}
              </span>
              <span className="font-medium text-green-500">{Math.round(dayStats.progress)}%</span>
              <span className="text-muted-foreground">{habitTasks.length} habit{habitTasks.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-green-500 transition-all"
                style={{ width: `${Math.round(dayStats.progress)}%` }}
              />
            </div>
          </div>
          </InfoTip>
          <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoTip label="Everything you finished today, including work that was never on the plan. Rescheduling doesn't reset it.">
              <Card className="cursor-help">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-primary">
                    {dayStats.completed}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Completed{dayStats.offPlanDone > 0 && (
                      <span className="block text-xs">incl. {dayStats.offPlanDone} unplanned</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </InfoTip>
            <InfoTip label="Planned tasks still to do. Doesn't count habits or meetings.">
              <Card className="cursor-help">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold">
                    {dayStats.remaining}
                  </div>
                  <div className="text-sm text-muted-foreground">Remaining</div>
                </CardContent>
              </Card>
            </InfoTip>
            <InfoTip label={dayStatsExplainer}>
              <Card className="cursor-help">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-green-500">
                    {Math.round(dayStats.progress)}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Progress
                    {dayStats.meetingsPlanned > 0 && (
                      <span className="block text-xs">
                        incl. {dayStats.meetingsDone}/{dayStats.meetingsPlanned} meeting
                        {dayStats.meetingsPlanned !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </InfoTip>
            <InfoTip label="Habits on today's plan. They hold their own slots and don't count towards the task numbers.">
              <Card className="cursor-help">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold">
                    {habitTasks.length}
                  </div>
                  <div className="text-sm text-muted-foreground">Habits</div>
                </CardContent>
              </Card>
            </InfoTip>
          </div>

          {/* Next Task Highlight */}
          {nextTask && nextTaskDetails && (
            <Card
              className="border-primary bg-primary/5"
              style={{
                borderLeftWidth: getProjectById(nextTaskDetails.project_id)?.color ? '4px' : undefined,
                borderLeftColor: getProjectById(nextTaskDetails.project_id)?.color || undefined,
              }}
            >
              {/* Desktop keeps the labeled card; phones get a slim two-line
                  banner (label+time, then title + icon-only complete button) */}
              <CardHeader className="hidden pb-2 md:block">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Up Next
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 py-2.5 md:px-6 md:pt-0 md:pb-4">
                <div className="md:hidden mb-0.5 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Up Next</span>
                  <span className="font-mono normal-case">{formatClock12(nextTask.start_time)}</span>
                  {energyIcons[nextTaskDetails.energy_level as keyof typeof energyIcons]}
                  {nextTaskDetails.is_habit && <UtensilsCrossed className="h-3 w-3" />}
                </div>
                <div className="flex flex-nowrap items-center gap-2 md:flex-wrap md:gap-4">
                  <div className="min-w-0 flex-1 md:basis-52">
                    <h3 className="truncate text-base font-semibold md:whitespace-normal md:text-lg">{nextTaskDetails.title}</h3>
                    <div className="hidden md:flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <Clock className="h-4 w-4" />
                        {formatClock12(nextTask.start_time)} - {formatClock12(nextTask.end_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        {energyIcons[nextTaskDetails.energy_level as keyof typeof energyIcons]}
                        {nextTaskDetails.energy_level}
                      </span>
                      {nextTaskDetails.is_habit && (
                        <Badge variant="secondary">Habit</Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    className="shrink-0"
                    size="sm"
                    title="Mark Complete"
                    onClick={() => completeFromToday(nextTask.task_id, nextTaskDetails.is_habit ? currentDate : undefined)}
                  >
                    <CheckCircle className="h-4 w-4 md:mr-2" />
                    <span className="hidden md:inline">{isToday && isWorkoutHabit(nextTaskDetails) ? 'Start workout' : 'Mark Complete'}</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Schedule Timeline (only in 'schedule' layout) */}
          {layoutMode === 'schedule' && (
          <Card>
            <CardHeader className="hidden md:block md:p-6 md:pb-2">
              <CardTitle className="flex items-center gap-2 text-base md:text-2xl">
                <Calendar className="h-5 w-5" />
                Today's Schedule
                <Badge variant="secondary" className="ml-2">
                  {filteredScheduledTasks.filter(st => !st.is_buffer).length} {scheduleFilter === 'all' ? 'tasks' : scheduleFilter}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-6 md:pt-0">
              {/* Overall Day Progress Bar */}
              {dayProgress && (
                <div className="mb-3 pb-3 border-b md:mb-4 md:pb-4">
                  <div className="flex items-center justify-center md:justify-between text-xs text-muted-foreground mb-2">
                    <span className="hidden md:inline font-mono">{formatClock12(dayProgress.startTime)}</span>
                    <div className="flex flex-col items-center">
                      <span className="text-sm font-bold text-red-500">
                        {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDuration(dayProgress.elapsed)} elapsed · {formatDuration(dayProgress.remaining)} remaining
                      </span>
                    </div>
                    <span className="hidden md:inline font-mono">{formatClock12(dayProgress.endTime)}</span>
                  </div>
                  <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                    {/* Progress fill */}
                    <div
                      className="absolute inset-y-0 left-0 bg-red-500/30 transition-all duration-1000"
                      style={{ width: `${dayProgress.percent}%` }}
                    />
                    {/* Current position indicator */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-red-500 rounded-full shadow-md transition-all duration-1000"
                      style={{ left: `${dayProgress.percent}%`, marginLeft: '-6px' }}
                    />
                  </div>
                  <div className="hidden md:block text-center text-xs text-muted-foreground mt-1">
                    {Math.round(dayProgress.percent)}% of scheduled day complete
                  </div>
                </div>
              )}

              {/* Task list with vertical day progress indicator */}
              <div className="flex gap-3">
                {/* Vertical day progress track */}
                {verticalProgress !== null && filteredScheduledTasks.length > 0 && (
                  <div className="relative w-2 flex-shrink-0">
                    {/* Track background */}
                    <div className="absolute inset-0 bg-muted rounded-full" />
                    {/* Progress fill - fills completed rows */}
                    <div
                      className="absolute top-0 left-0 right-0 bg-red-500/40 rounded-full transition-all duration-1000"
                      style={{ height: `${verticalProgress.dotPosition}%` }}
                    />
                    {/* Current position marker */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-red-500 rounded-full shadow-lg border-2 border-background z-10 transition-all duration-1000"
                      style={{ top: `${verticalProgress.dotPosition}%`, marginTop: '-8px' }}
                    >
                      {/* Pulse animation */}
                      <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-30" />
                    </div>
                  </div>
                )}

                {/* Task list — min-w-0 lets rows shrink so titles truncate
                    instead of forcing the whole column past the viewport */}
                <div className="min-w-0 flex-1 space-y-2">
                  {timelineRows.length === 0 ? (
                    <EmptyState
                      icon={Calendar}
                      title={scheduleFilter === 'all' ? emptyStateConfigs.schedule.title : `No ${scheduleFilter} scheduled`}
                      // An empty plan with a reason says the reason. The
                      // scheduler correctly refuses to book work into hours
                      // you've said you don't work — but silently, so pressing
                      // Generate at 11pm looked like a broken button.
                      description={scheduleFilter === 'all'
                        ? (planEmptyReason ?? emptyStateConfigs.schedule.description)
                        : `No ${scheduleFilter} are scheduled for today. Try selecting "All" to see the full schedule.`}
                      action={scheduleFilter === 'all' && !planEmptyReason ? {
                        label: 'Reschedule Now',
                        onClick: onReschedule,
                        icon: RefreshCw,
                      } : undefined}
                      compact
                    />
                  ) : (
                    timelineRows.map((row) => {
                    if (row.kind === 'events') {
                      return (
                        <CalendarEventGroup
                          key={row.events[0].id}
                          events={row.events}
                          colorFor={sourceColor}
                          nameFor={sourceName}
                          hasPreference={groupHasPreference(row.events, sourcesById)}
                        />
                      )
                    }
                    const { scheduled, index } = row
                    const task = getTaskById(scheduled.task_id)
                    const completed = isTaskCompleted(scheduled.task_id)
                    const isPast = !completed && isTaskInPast(scheduled)
                    const isCurrentTaskRow = currentTimeIndicator?.taskIndex === index

                    if (scheduled.is_buffer) {
                      return (
                        <div key={`buffer-${scheduled.start_time}`} className="relative overflow-hidden rounded-lg">
                          {/* Progress fill background for current task */}
                          {isCurrentTaskRow && (
                            <div
                              className="absolute inset-0 bg-red-500/10 pointer-events-none transition-all duration-1000"
                              style={{ clipPath: `inset(0 ${100 - currentTimeIndicator.progressPercent}% 0 0)` }}
                            />
                          )}
                          {/* Current time indicator line for this row */}
                          {isCurrentTaskRow && (
                            <div
                              className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
                              style={{ left: `${currentTimeIndicator.progressPercent}%` }}
                            >
                              <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500" />
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-red-500 font-medium whitespace-nowrap">Now</span>
                            </div>
                          )}
                          <div className="relative flex items-center gap-2 py-2 px-3 bg-muted/50 text-sm text-muted-foreground md:gap-4 md:px-4">
                            <div className="w-16 shrink-0 whitespace-nowrap text-right font-mono text-xs md:w-20">
                              {formatClock12(scheduled.start_time)}
                            </div>
                            <div className="w-5 h-5 rounded-full border-2 border-dashed border-muted-foreground/30 flex-shrink-0" />
                            <div className="flex-1">
                              <span>Break</span>
                            </div>
                            <div className="text-xs flex items-center gap-2">
                              <span>{formatDuration(scheduled.estimated_minutes)}</span>
                              {isCurrentTaskRow && currentTimeIndicator.remainingMinutes > 0 && (
                                <span className="text-red-500 font-medium">
                                  ({formatDuration(currentTimeIndicator.remainingMinutes)} left)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    }

                    const project = task ? getProjectById(task.project_id) : null
                    // Habits and hard pins keep their slot even when a meeting
                    // lands on it; this is how the user finds out.
                    const clashesWith = busyConflicts(
                      scheduled.start_time,
                      scheduled.end_time,
                      dayBusySlots,
                    )
                    const hasDeadline = task?.deadline
                    // Core's reading, in the user's zone — so the date shown
                    // is the date Today, "overdue" and the scheduler all use.
                    const deadlineDate = task ? deadlines.date(task) : null
                    const isOverdue = !!task && deadlines.overdue(task)
                    const isDeadlineToday = !!task && deadlines.daysLeft(task) === 0

                    return (
                      <div key={scheduled.task_id} className="relative overflow-hidden rounded-lg">
                        {/* Progress fill background for current task */}
                        {isCurrentTaskRow && !completed && (
                          <div
                            className="absolute inset-0 bg-red-500/10 pointer-events-none transition-all duration-1000"
                            style={{ clipPath: `inset(0 ${100 - currentTimeIndicator.progressPercent}% 0 0)` }}
                          />
                        )}
                        {/* Current time indicator line for this row */}
                        {isCurrentTaskRow && !completed && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
                            style={{ left: `${currentTimeIndicator.progressPercent}%` }}
                          >
                            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500" />
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-red-500 font-medium whitespace-nowrap">Now</span>
                          </div>
                        )}
                        <div
                          className={cn(
                            // Compact chrome on phones (narrow time col, no end-time
                            // col, small gaps) so the title/badges get real width
                            // instead of wrapping into a skinny column / overflowing
                            "relative flex items-center gap-2 py-2.5 px-3 border transition-all cursor-pointer md:gap-4 md:px-4 md:py-3",
                            completed
                              ? "bg-green-500/10 border-green-500/30"
                              : isPast
                              ? "bg-orange-500/5 border-orange-500/20 opacity-70"
                              : "hover:bg-accent hover:shadow-sm"
                          )}
                          style={{
                            borderLeftWidth: project?.color ? '4px' : undefined,
                            borderLeftColor: project?.color || undefined,
                          }}
                          onClick={() => handleTaskClick(scheduled)}
                        >
                          {/* Time */}
                          <div className="w-16 shrink-0 whitespace-nowrap text-right font-mono text-xs text-muted-foreground md:w-20">
                            {formatClock12(scheduled.start_time)}
                          </div>

                          {/* Completion checkbox */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              // Tapping a finished one takes it back. Same
                              // control both ways — a tick you can't untick
                              // is a trap on the app's most-used button.
                              const forDate = task?.is_habit ? currentDate : undefined
                              if (completed) onUncompleteTask?.(scheduled.task_id, forDate)
                              else completeFromToday(scheduled.task_id, forDate)
                            }}
                            className={cn(
                              "flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors",
                              completed
                                ? "bg-green-500 border-green-500 text-white"
                                : "border-muted-foreground hover:border-primary hover:bg-primary/10"
                            )}
                          >
                            {completed && <CheckCircle className="h-4 w-4" />}
                          </button>

                          {/* Task info — single truncated line on phones
                              (details are one tap away in the task dialog);
                              wrapping title + badges + project line on md+ */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 md:gap-2 flex-nowrap md:flex-wrap">
                              {/* Hovering the title says what the row's
                                  pictograms mean — project, urgency, energy,
                                  overdue-ness — the same sentence every task
                                  list uses. On a phone the row already opens
                                  the detail dialog, which carries all of it. */}
                              {task ? (
                                <TaskInfoTip task={task} scheduledFor={currentDate}>
                                  <span className={cn(
                                    "min-w-0 truncate font-medium md:overflow-visible md:whitespace-normal",
                                    completed && "line-through text-muted-foreground"
                                  )}>
                                    {task.title}
                                  </span>
                                </TaskInfoTip>
                              ) : (
                              <span className={cn(
                                "min-w-0 truncate font-medium md:overflow-visible md:whitespace-normal",
                                completed && "line-through text-muted-foreground"
                              )}>
                                Unknown Task
                              </span>
                              )}
                              {/* Show habit instance detail (e.g., "Lunch - Fried Rice")
                                  — desktop only; on phones it's in the tap-through dialog */}
                              {task?.is_habit && (
                                <span className="hidden md:inline-flex">
                                  <HabitInstanceBadge
                                    key={instanceRefreshKey}
                                    habitId={task.id}
                                    date={currentDate}
                                    onEdit={() => setEditingHabit(task)}
                                  />
                                </span>
                              )}
                              <span className="shrink-0">{task && energyIcons[task.energy_level as keyof typeof energyIcons]}</span>
                              {task?.is_habit && (
                                <Badge
                                  variant="secondary"
                                  className="shrink-0 text-xs cursor-pointer hover:bg-secondary/80"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (task) setEditingHabit(task)
                                  }}
                                >
                                  <UtensilsCrossed className="h-3 w-3 md:mr-1" />
                                  <span className="hidden md:inline">Habit</span>
                                </Badge>
                              )}
                              {isPast && (
                                <Badge
                                  variant="outline"
                                  className="shrink-0 text-xs text-orange-500 border-orange-500/50"
                                >
                                  <Clock className="h-3 w-3 md:mr-1" />
                                  <span className="hidden md:inline">Missed</span>
                                </Badge>
                              )}
                              {clashesWith.length > 0 && !completed && (
                                <Badge
                                  variant="outline"
                                  className="shrink-0 text-xs text-amber-500 border-amber-500/50"
                                  title={`Overlaps ${clashesWith.join(', ')}`}
                                >
                                  <CalendarClock className="h-3 w-3 md:mr-1" />
                                  <span className="hidden md:inline">Clash</span>
                                </Badge>
                              )}
                            </div>
                            {/* Project and Deadline info — desktop only: on phones
                                the project is the row's colored left border and the
                                rest lives in the tap-through detail dialog */}
                            <div className="hidden md:flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              {clashesWith.length > 0 && !completed && (
                                <span className="flex items-center gap-1 text-amber-500">
                                  <CalendarClock className="h-3 w-3" />
                                  Overlaps {clashesWith.join(', ')}
                                </span>
                              )}
                              {project && (
                                <span className="flex items-center gap-1">
                                  <FolderOpen className="h-3 w-3" />
                                  {project.name}
                                </span>
                              )}
                              {hasDeadline && (
                                <span className={cn(
                                  "flex items-center gap-1",
                                  isOverdue && "text-red-500",
                                  isDeadlineToday && "text-orange-500"
                                )}>
                                  <Calendar className="h-3 w-3" />
                                  {deadlineDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  {isOverdue && " (overdue)"}
                                  {isDeadlineToday && " (today)"}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Duration and remaining time */}
                          <div className="shrink-0 text-xs md:text-sm text-muted-foreground flex flex-col items-end">
                            {completed && task ? (
                              <ActualTimeEditor task={task} estimatedMinutes={scheduled.estimated_minutes} />
                            ) : (
                              <span>{formatDuration(scheduled.estimated_minutes)}</span>
                            )}
                            {isCurrentTaskRow && !completed && currentTimeIndicator.remainingMinutes > 0 && (
                              <span className="text-xs text-red-500 font-medium">
                                {formatDuration(currentTimeIndicator.remainingMinutes)} left
                              </span>
                            )}
                          </div>

                          {/* End time — redundant on phones (start + duration shown) */}
                          <div className="hidden w-20 font-mono text-xs text-muted-foreground md:block">
                            {formatClock12(scheduled.end_time)}
                          </div>
                        </div>
                      </div>
                    )
                    })
                  )}
                </div>

              </div>

                {/* Rows a reschedule retired: finished before their slot, so
                    the scheduler released that time to other work and
                    re-anchored them to when they were actually done. Bottom of
                    the list, full width, collapsed — a receipt under the plan,
                    never beside it. Ticking a future task does NOT put it
                    here; only rescheduling does. */}
                {doneAheadTasks.length > 0 && (
                  <div data-testid="done-ahead" className="mt-3 border-t pt-2">
                    <button
                      type="button"
                      onClick={() => setShowDoneAhead(v => !v)}
                      aria-expanded={showDoneAhead}
                      className="flex w-full min-h-[40px] items-center gap-2 text-sm text-muted-foreground hover:text-foreground md:min-h-0"
                    >
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Done ahead of schedule ({doneAheadTasks.length})
                      <span className="text-xs">{showDoneAhead ? '▾' : '▸'}</span>
                    </button>
                    {showDoneAhead && (
                      <div className="mt-2 space-y-1">
                        {doneAheadTasks.map((scheduled) => {
                          const task = getTaskById(scheduled.task_id)
                          return (
                            <div
                              key={`ahead-${scheduled.task_id}`}
                              className="flex items-center gap-2 rounded-lg border border-green-500/20 bg-green-500/5 px-3 py-2 cursor-pointer"
                              onClick={() => handleTaskClick(scheduled)}
                            >
                              <span className="w-16 shrink-0 text-right font-mono text-xs text-muted-foreground md:w-20">
                                {formatClock12(scheduled.start_time)}
                              </span>
                              {/* Untickable here too — undo must work wherever
                                  the tick is visible. */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const forDate = task?.is_habit ? currentDate : undefined
                                  onUncompleteTask?.(scheduled.task_id, forDate)
                                }}
                                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-green-500 bg-green-500 text-white"
                                aria-label={`Undo ${task?.title ?? 'task'}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                              {task ? (
                                <TaskInfoTip task={task} scheduledFor={currentDate}>
                                  <span className="min-w-0 flex-1 truncate text-sm line-through text-muted-foreground">
                                    {task.title}
                                  </span>
                                </TaskInfoTip>
                              ) : (
                                <span className="min-w-0 flex-1 truncate text-sm line-through text-muted-foreground">
                                  Unknown task
                                </span>
                              )}
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {formatDuration(scheduled.estimated_minutes)}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
            </CardContent>
          </Card>
          )}

          {/* Project-grouped layout (Todoist style) */}
          {layoutMode === 'projects' && (
            <TodayByProject
              schedule={schedule}
              tasks={tasks}
              projects={projects}
              currentDate={currentDate}
              scheduleFilter={scheduleFilter}
              isTaskCompleted={isTaskCompleted}
              onCompleteTask={onCompleteTask}
              onUncompleteTask={onUncompleteTask}
              onTaskClick={handleTaskClick}
            />
          )}

          {/* Task Finder */}
          <TaskFinder
            tasks={tasks}
            projects={projects}
            scheduledTasks={schedule.scheduled_tasks || []}
            onSelectTask={(task) => {
              // Could add functionality to highlight/scroll to task
              console.log('Selected task:', task)
            }}
          />
        </>
      )}

      {/* Habit Instance Editor Dialog */}
      {editingHabit && (
        <HabitInstanceEditor
          habit={editingHabit}
          date={currentDate}
          isOpen={!!editingHabit}
          onClose={() => setEditingHabit(null)}
          onSave={() => setInstanceRefreshKey(k => k + 1)}
        />
      )}

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        task={selectedTask || null}
        scheduledTask={selectedScheduledTask}
        project={selectedProject}
        isOpen={!!selectedTaskId}
        onClose={handleCloseDetail}
        onEdit={onEditTask}
        onComplete={(taskId) => completeFromToday(taskId, selectedTask?.is_habit ? currentDate : undefined)}
        isCompleted={selectedTaskId ? isTaskCompleted(selectedTaskId) : false}
      />
      </div>

      {/* Right Sidebar */}
      <TodaySidebar
        selectedDate={selectedDateObj}
        onDateSelect={handleSidebarDateSelect}
        onOpenTask={setSelectedTaskId}
      />
    </div>
  )
}
